import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { featuredArticles } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.hair';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'YOYBUY Spreadsheet: полное руководство 2026',
  description: 'Полное руководство по YOYBUY Spreadsheet — инновационной системе управления заказами из Китая. Узнайте, как работает YOYBUY Sheet, сравнение с Kakobuy и Superbuy, пошаговая инструкция и ответы на частые вопросы.',
  author: {
    '@type': 'Organization',
    name: 'yoybuy.hair',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'yoybuy.hair',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/article/yoybuy-spreadsheet-guide`,
  },
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Что такое YOYBUY Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'YOYBUY Spreadsheet — это электронная таблица для управления заказами из Китая. Она объединяет функции каталога, трекера доставки и калькулятора стоимости в одном интерфейсе.',
      },
    },
    {
      '@type': 'Question',
      name: 'Чем YOYBUY Spreadsheet отличается от обычных приложений?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'В отличие от приложений, YOYBUY Sheet предоставляет полную таблицу со всеми заказами одновременно. Вы видите всю картину целиком, можете сортировать по любым параметрам и экспортировать данные.',
      },
    },
    {
      '@type': 'Question',
      name: 'Какая комиссия у YOYBUY Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Комиссия YOYBUY Sheet зависит от категории товара и способа доставки. В среднем она сопоставима с конкурентами, но прозрачность расчётов позволяет лучше контролировать расходы.',
      },
    },
    {
      '@type': 'Question',
      name: 'Безопасно ли использовать YOYBUY Spreadsheet?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Да, YOYBUY Sheet использует современные протоколы шифрования для защиты данных пользователей. Все платёжные транзакции проходят через защищённые каналы.',
      },
    },
    {
      '@type': 'Question',
      name: 'Что лучше: YOYBUY, Kakobuy или Superbuy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Выбор зависит от ваших задач. YOYBUY Sheet лучше всего подходит для тех, кто ценит прозрачность и структурированный подход. Kakobuy предлагает более низкие комиссии, а Superbuy — удобный мобильный интерфейс. Рекомендуем попробовать все три и выбрать наиболее подходящий для вас вариант.',
      },
    },
  ],
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />

      <article>
        {/* Article Hero */}
        <section className="w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-400"></span>
              <a href="/" className="text-xs text-foreground-400 font-label font-medium hover:text-accent-500 transition-colors duration-200 whitespace-nowrap">
                {t('breadcrumbHome')}
              </a>
              <span className="text-xs text-foreground-300">/</span>
              <span className="text-xs text-foreground-400 font-label font-medium">
                {t('breadcrumbArticles')}
              </span>
              <span className="text-xs text-foreground-300">/</span>
              <span className="text-xs text-foreground-500 font-label font-medium">
                YOYBUY Spreadsheet
              </span>
              <a
                href="/"
                className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 transition-colors duration-200 cursor-pointer"
                aria-label={t('backToHome')}
              >
                <i className="ri-arrow-left-line text-foreground-500 text-xs"></i>
              </a>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-foreground-900 font-light leading-tight mb-6">
              YOYBUY Spreadsheet:
              <br />
              полное руководство 2026
            </h1>

            <p className="text-sm md:text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed">
              YOYBUY Spreadsheet — это инновационная система управления заказами, которая меняет подход к покупкам из Китая. В отличие от обычных мобильных приложений, spreadsheet-формат даёт пользователю полный контроль над каждым этапом — от выбора товара до его доставки на дом. Это руководство создано для тех, кто хочет понять, как работает YOYBUY Sheet, и научиться использовать его максимально эффективно.
            </p>
          </div>
        </section>

        {/* Article Body */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <span className="inline-block px-5 py-2 border border-foreground-300/60 rounded-full text-xs text-foreground-500 font-label font-medium uppercase tracking-wider mb-10">
              SEO Guide V3
            </span>

            {/* H2: Что такое YOYBUY Spreadsheet */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-semibold mb-6 text-center">
              Что такое YOYBUY Spreadsheet и как он работает
            </h2>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-6">
              <strong className="text-foreground-900">YOYBUY Spreadsheet</strong> представляет собой электронную таблицу, которая объединяет функции каталога товаров, системы отслеживания заказов и инструмента сравнения цен. В отличие от стандартных интерфейсов интернет-магазинов, YOYBUY Sheet даёт пользователю возможность видеть всю информацию о заказе в структурированном виде: название товара, цена в юанях и в местной валюте, статус обработки, ориентировочная дата доставки и трек-номер.
            </p>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-6">
              Основное преимущество <strong className="text-foreground-900">YOYBUY Spreadsheet</strong> заключается в его прозрачности. Каждый столбец таблицы соответствует определённому этапу логистической цепочки: заказ у поставщика, прибытие на склад, проверка качества, упаковка и международная отправка. Пользователь в реальном времени видит, на каком этапе находится его товар, что исключает неопределённость и беспокойство.
            </p>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-10">
              Система работает по принципу единого окна: вы выбираете товары на китайских платформах, добавляете их в <strong className="text-foreground-900">YOYBUY Sheet</strong>, а система автоматически отслеживает статус, рассчитывает комиссии и формирует итоговую стоимость с учётом доставки. Это особенно удобно для тех, кто делает регулярные заказы или управляет небольшим бизнесом по перепродаже товаров из Китая.
            </p>

            {/* Сравнительная таблица */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-semibold mb-6 text-center">
              Сравнение YOYBUY, Kakobuy и Superbuy Spreadsheet
            </h2>

            <div className="overflow-x-auto mb-10 rounded-lg border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-foreground-900 text-background-50">
                    <th className="text-left px-4 py-3 font-label font-semibold">Параметр</th>
                    <th className="text-left px-4 py-3 font-label font-semibold">YOYBUY</th>
                    <th className="text-left px-4 py-3 font-label font-semibold">Kakobuy</th>
                    <th className="text-left px-4 py-3 font-label font-semibold">Superbuy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-background-50 border-b border-background-200/60">
                    <td className="px-4 py-3 font-medium text-foreground-800 font-label">Удобство интерфейса</td>
                    <td className="px-4 py-3 text-foreground-600">Высокое</td>
                    <td className="px-4 py-3 text-foreground-600">Среднее</td>
                    <td className="px-4 py-3 text-foreground-600">Высокое</td>
                  </tr>
                  <tr className="bg-background-100 border-b border-background-200/60">
                    <td className="px-4 py-3 font-medium text-foreground-800 font-label">Комиссия за заказ</td>
                    <td className="px-4 py-3 text-foreground-600">Средняя</td>
                    <td className="px-4 py-3 text-foreground-600">Низкая</td>
                    <td className="px-4 py-3 text-foreground-600">Средняя</td>
                  </tr>
                  <tr className="bg-background-50 border-b border-background-200/60">
                    <td className="px-4 py-3 font-medium text-foreground-800 font-label">Скорость обработки</td>
                    <td className="px-4 py-3 text-foreground-600">Быстрая</td>
                    <td className="px-4 py-3 text-foreground-600">Средняя</td>
                    <td className="px-4 py-3 text-foreground-600">Быстрая</td>
                  </tr>
                  <tr className="bg-background-100 border-b border-background-200/60">
                    <td className="px-4 py-3 font-medium text-foreground-800 font-label">Поддержка клиентов</td>
                    <td className="px-4 py-3 text-foreground-600">24/7 чат</td>
                    <td className="px-4 py-3 text-foreground-600">Email</td>
                    <td className="px-4 py-3 text-foreground-600">Тикет-система</td>
                  </tr>
                  <tr className="bg-background-50">
                    <td className="px-4 py-3 font-medium text-foreground-800 font-label">Отслеживание заказов</td>
                    <td className="px-4 py-3 text-foreground-600">Полное</td>
                    <td className="px-4 py-3 text-foreground-600">Базовое</td>
                    <td className="px-4 py-3 text-foreground-600">Полное</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* H2: Преимущества YOYBUY Spreadsheet */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-semibold mb-6 text-center">
              <span className="w-7 h-7 inline-flex items-center justify-center align-middle mr-2">
                <i className="ri-bar-chart-box-line text-accent-500 text-xl"></i>
              </span>
              Преимущества YOYBUY Spreadsheet
            </h2>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-4">
              <strong className="text-foreground-900">YOYBUY Sheet</strong> предлагает ряд уникальных преимуществ, которые выделяют его на фоне конкурентов. Первое и самое очевидное — это полная прозрачность всех операций. В отличие от систем, где статус заказа скрыт за несколькими меню, YOYBUY Spreadsheet показывает всё в одной таблице.
            </p>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-4">
              Второе преимущество — возможность массового управления заказами. Если вы заказываете десятки или сотни позиций одновременно, <strong className="text-foreground-900">YOYBUY Spreadsheet</strong> позволяет сортировать, фильтровать и группировать заказы по любым параметрам: статусу, цене, дате, поставщику. Это незаменимый инструмент для бизнеса.
            </p>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-4">
              Третье — интеграция с основными китайскими торговыми платформами. <strong className="text-foreground-900">YOYBUY Sheet</strong> автоматически подтягивает информацию о товарах, ценах и наличии, что исключает ручной ввод данных и связанные с этим ошибки.
            </p>

            <p className="text-sm md:text-base text-foreground-600 font-body leading-relaxed mb-10">
              Четвёртое — гибкая система расчёта стоимости доставки. YOYBUY Spreadsheet учитывает вес, габариты и способ отправки каждого товара, позволяя выбрать оптимальный вариант по соотношению цена/скорость. Вы всегда видите финальную стоимость до того, как подтвердите заказ.
            </p>

            {/* H2: Как использовать YOYBUY Sheet */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-semibold mb-6 text-center">
              <span className="w-7 h-7 inline-flex items-center justify-center align-middle mr-2">
                <i className="ri-file-list-3-line text-accent-500 text-xl"></i>
              </span>
              Как использовать YOYBUY Spreadsheet шаг за шагом
            </h2>

            <div className="space-y-5 mb-10">
              <div className="flex gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-label font-bold text-sm shrink-0">1</span>
                <div>
                  <h3 className="font-label font-semibold text-foreground-900 text-sm mb-1">Выбор товаров</h3>
                  <p className="text-sm text-foreground-600 font-body leading-relaxed">Найдите нужные товары на китайских платформах и скопируйте ссылки в YOYBUY Sheet. Система автоматически распознает артикулы, цены и параметры товаров.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-label font-bold text-sm shrink-0">2</span>
                <div>
                  <h3 className="font-label font-semibold text-foreground-900 text-sm mb-1">Добавление в таблицу</h3>
                  <p className="text-sm text-foreground-600 font-body leading-relaxed">После обработки ссылок YOYBUY Sheet создаёт строки с детальной информацией о каждом товаре. Проверьте данные и подтвердите добавление.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-label font-bold text-sm shrink-0">3</span>
                <div>
                  <h3 className="font-label font-semibold text-foreground-900 text-sm mb-1">Отслеживание статуса</h3>
                  <p className="text-sm text-foreground-600 font-body leading-relaxed">Каждый этап отображается в реальном времени: заказ у поставщика, прибытие на склад, проверка качества. Вы всегда знаете, где находятся ваши товары.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-label font-bold text-sm shrink-0">4</span>
                <div>
                  <h3 className="font-label font-semibold text-foreground-900 text-sm mb-1">Выбор доставки и оплата</h3>
                  <p className="text-sm text-foreground-600 font-body leading-relaxed">YOYBUY Sheet рассчитывает стоимость разных способов доставки. Выберите оптимальный вариант, оплатите заказ и ждите получения.</p>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 font-semibold mb-8 text-center">
              Часто задаваемые вопросы (FAQ)
            </h2>

            <div className="space-y-5 mb-12">
              <div className="bg-background-100 rounded-lg p-5">
                <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">
                  Что такое YOYBUY Spreadsheet?
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  <strong className="text-foreground-900">YOYBUY Spreadsheet</strong> — это электронная таблица для управления заказами из Китая. Она объединяет функции каталога, трекера доставки и калькулятора стоимости в одном интерфейсе.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">
                  Чем YOYBUY Spreadsheet отличается от обычных приложений?
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  В отличие от приложений, <strong className="text-foreground-900">YOYBUY Sheet</strong> предоставляет полную таблицу со всеми заказами одновременно. Вы видите всю картину целиком, можете сортировать по любым параметрам и экспортировать данные.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">
                  Какая комиссия у YOYBUY Spreadsheet?
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  Комиссия <strong className="text-foreground-900">YOYBUY Sheet</strong> зависит от категории товара и способа доставки. В среднем она сопоставима с конкурентами, но прозрачность расчётов позволяет лучше контролировать расходы.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">
                  Безопасно ли использовать YOYBUY Spreadsheet?
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  Да, <strong className="text-foreground-900">YOYBUY Sheet</strong> использует современные протоколы шифрования для защиты данных пользователей. Все платёжные транзакции проходят через защищённые каналы.
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">
                  Что лучше: YOYBUY, Kakobuy или Superbuy?
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  Выбор зависит от ваших задач. <strong className="text-foreground-900">YOYBUY Sheet</strong> лучше всего подходит для тех, кто ценит прозрачность и структурированный подход. Kakobuy предлагает более низкие комиссии, а Superbuy — удобный мобильный интерфейс. Рекомендуем попробовать все три и выбрать наиболее подходящий для вас вариант.
                </p>
              </div>
            </div>

            {/* CTA + Back to home */}
            <div className="text-center mb-10">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 rounded-full text-sm font-label font-semibold hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer mb-6"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-sm"></i>
                </span>
                Перейти на YOYBUY Spreadsheet
              </a>
              <br />
              <a
                href="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-500 font-body hover:text-accent-500 transition-colors duration-200"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </span>
                {t('backToHome')}
              </a>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-10">
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 font-semibold leading-tight shrink-0">
                {t('articleReadAlso')}
              </h2>
              <p className="text-sm text-foreground-500 font-body max-w-md leading-relaxed">
                {t('articleReadAlsoDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {featuredArticles.map((article) => {
                if (article.slug === 'yoybuy-spreadsheet-guide') {
                  return (
                    <Link
                      key={article.slug}
                      to="/article/kakobuy-spreadsheet-guide"
                      className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer text-left hover:bg-background-200/70 transition-colors duration-300 block"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src="https://readdy.ai/api/search-image?query=Modern%20desk%20with%20laptop%20showing%20spreadsheet%20and%20order%20tracking%20dashboard%2C%20notebook%20and%20coffee%2C%20warm%20natural%20lighting%20from%20side%2C%20minimalist%20aesthetic%2C%20soft%20cream%20and%20beige%20tones%2C%20lifestyle%20editorial%20photography&width=600&height=400&seq=article-related-01&orientation=landscape"
                          alt="Kakobuy Spreadsheet руководство"
                          title="Kakobuy Spreadsheet руководство"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 text-foreground-700 rounded-full text-xs font-label font-medium">
                          Kakobuy
                        </span>
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading text-lg text-foreground-900 font-semibold group-hover:text-accent-500 transition-colors duration-300">
                          Kakobuy Spreadsheet: руководство для начинающих
                        </h3>
                      </div>
                    </Link>
                  );
                }
                return (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer text-left hover:bg-background-200/70 transition-colors duration-300 block"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 text-foreground-700 rounded-full text-xs font-label font-medium">
                        {article.category}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-lg text-foreground-900 font-semibold group-hover:text-accent-500 transition-colors duration-300">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20shipping%20and%20logistics%20scene%20with%20organized%20packages%20and%20boxes%20in%20soft%20light%2C%20cream%20and%20beige%20aesthetic%2C%20clean%20industrial%20minimalism%2C%20gentle%20shadows%2C%20professional%20editorial%20style%20photography%20with%20dreamy%20atmosphere&width=1600&height=900&seq=cta-article-bg&orientation=landscape"
            alt="Начните использовать YOYBUY Sheet"
            title="YOYBUY Sheet сегодня"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>

          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-3xl md:text-5xl text-background-50 font-bold leading-tight mb-4 tracking-wide">
              НАЧНИТЕ ИСПОЛЬЗОВАТЬ YOYBUY SHEET СЕГОДНЯ
            </h2>
            <p className="text-background-50/75 text-sm md:text-base font-body mb-10 max-w-xl mx-auto leading-relaxed">
              Присоединяйтесь к сообществу и откройте новый уровень удобства при заказах из Китая
            </p>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-3 px-8 py-4 bg-foreground-900 text-background-50 rounded-full text-sm font-label font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer group"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 shrink-0">
                <i className="ri-shopping-bag-line text-background-50 text-sm"></i>
              </span>
              <span>ОТКРОЙТЕ KASPREADSHEET.COM</span>
              <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300">
                <i className="ri-arrow-right-up-line text-background-50 text-sm"></i>
              </span>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </main>
  );
}